import { assertAuthorizedOrClearSession } from '@/infrastructure/auth/unauthorized-session-bridge';

// Bound the self-service profile round-trips from the browser. The BFF route
// already enforces its own upstream timeout; this is the client-side guard so
// the user sees a thrown error instead of a tab that spins forever when the
// network or BFF stalls. Mirrors the constant in
// `features/user-management/api.ts`.
const REQUEST_TIMEOUT_MS = 30_000;

/**
 * Shape returned by `GET /api/me/profile` (BFF proxy to `auth-api-gateway`
 * `GET /v1.0/profile`). Mirrors the backend `GetUserProfileResponseDto`.
 *
 * Defined locally so the platform-admin feature module does not need a
 * compile-time dependency on the backend DTO package.
 */
export interface MyProfileResponse {
  uuid: string;
  username: string;
  email: string;
  firstName: string;
  lastName: string;
  initials: string;
  emailConfirmed: boolean;
  creationDatetime: string;
  deletionStatus: boolean;
  deletionRecycleStatus: boolean;
  phoneNumber?: string;
  /** Raw value stored on `tenant_users.picture` — UUID, URL, or null. */
  picture?: string | null;
  /** Short-lived presigned URL (or external URL passthrough). Refresh by refetching the query. */
  pictureUrl?: string | null;
}

export interface UpdateMyProfilePictureResponse {
  picture: string | null;
  pictureUrl: string | null;
}

/**
 * Phone payload accepted by `PATCH /api/me/profile`. Mirrors
 * `AdminProfilePhoneDto` in `@microservices/dtos` — keep in sync if the
 * backend contract changes.
 */
export interface UpdateMyProfilePhone {
  countryCode: string;
  phoneNumber: string;
}

/**
 * Request body for `PATCH /api/me/profile` (BFF proxy to
 * `PATCH /auth/v1.0/me/profile`). Intentionally omits the `picture` and
 * `alternateCommunicationEmail` fields the backend also accepts — the
 * platform-admin self-service form does not surface them.
 *
 * All keys are optional: absent fields are left untouched on the
 * `tenant_users` row. Pass `phone: null` to leave the existing primary
 * phone unchanged (per the backend contract, removing a phone goes
 * through a dedicated delete flow, not this endpoint).
 */
export interface UpdateMyProfileRequest {
  firstName?: string;
  lastName?: string;
  initials?: string;
  phone?: UpdateMyProfilePhone | null;
}

export interface UpdateMyProfileResponse {
  userUuid: string;
  changedFields: string[];
}

export async function fetchMyProfile(): Promise<MyProfileResponse> {
  const response = await fetch('/api/me/profile', {
    credentials: 'include',
    signal: AbortSignal.timeout(REQUEST_TIMEOUT_MS),
  });
  assertAuthorizedOrClearSession(response);
  if (!response.ok) {
    throw new Error(`Failed to load profile: ${response.status}`);
  }
  return response.json();
}

export async function uploadMyProfilePicture(
  file: File,
): Promise<UpdateMyProfilePictureResponse> {
  const formData = new FormData();
  formData.append('file', file, file.name);

  const response = await fetch('/api/me/profile/picture', {
    method: 'POST',
    body: formData,
    credentials: 'include',
    signal: AbortSignal.timeout(REQUEST_TIMEOUT_MS),
  });
  assertAuthorizedOrClearSession(response);
  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw new Error(
      (error as { message?: string }).message ??
        `Failed to upload profile picture: ${response.status}`,
    );
  }
  return response.json();
}

export async function updateMyProfile(
  patch: UpdateMyProfileRequest,
): Promise<UpdateMyProfileResponse> {
  const response = await fetch('/api/me/profile', {
    method: 'PATCH',
    credentials: 'include',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify(patch),
    signal: AbortSignal.timeout(REQUEST_TIMEOUT_MS),
  });
  assertAuthorizedOrClearSession(response);
  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw new Error(
      (error as { message?: string }).message ??
        `Failed to update profile: ${response.status}`,
    );
  }
  return response.json();
}

export async function deleteMyProfilePicture(): Promise<UpdateMyProfilePictureResponse> {
  const response = await fetch('/api/me/profile/picture', {
    method: 'DELETE',
    credentials: 'include',
    signal: AbortSignal.timeout(REQUEST_TIMEOUT_MS),
  });
  assertAuthorizedOrClearSession(response);
  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw new Error(
      (error as { message?: string }).message ??
        `Failed to remove profile picture: ${response.status}`,
    );
  }
  return response.json();
}
